import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';

export const getEnrichedPackageSearchPath = (meta: MetaQuery | undefined) =>
  meta?.customerOrdersExport ? '/customer-orders/export/search' : '/searchV2';

class OrderPackageAPIClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/package');
  }

  override async search<T = any, Q = any>(
    query: Q,
    page: number,
    size: number,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    if (!meta?.enrichItems) {
      return super.search<T, Q>(query, page, size, meta);
    }

    const searchPath = getEnrichedPackageSearchPath(meta);
    const response = await axiosInstance.post(
      `${this.baseURL}${searchPath}?page=${page}&size=${size}&enrichItems=true`,
      query,
      this.getRequestConfig(meta),
    );
    return this.transformList<T>(response.data.data);
  }

  async markDelivered(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/delivered';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async revertDeliveredToDispatched(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/revert-delivered-to-dispatched';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async moveBackToPacked(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/move-back-to-packed';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async resumeRtoDelivery(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = this.baseURL + '/' + meta?.packageId + '/rto/resume-delivery';
    const response = await axiosInstance.put(`${url}`, payload ?? {});
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(
        response.data?.status?.message ||
          response.data?.status?.key ||
          'Unable to resume package delivery.',
      );
    }
    return this.transformNode(response.data?.data);
  }

  async confirmRtoDelivery(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = this.baseURL + '/' + meta?.packageId + '/rto/confirm-delivered';
    const response = await axiosInstance.put(`${url}`, payload ?? {});
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(
        response.data?.status?.message ||
          response.data?.status?.key ||
          'Unable to confirm package delivery.',
      );
    }
    return this.transformNode(response.data?.data);
  }

  async repairTahReturnedItems(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/try-at-home/repair-returned-items';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
        throw new Error(
          response.data?.status?.message ||
            response.data?.status?.key ||
            'Unable to repair TAH returned items.',
        );
      }
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async quoteTahCouponReprice(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/try-at-home/coupon-reprice-quote';
      const response = await axiosInstance.post(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async quoteTahAdminCoupon(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/try-at-home/admin-coupon-quote';
      const response = await axiosInstance.post(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async suggestTahAdminCoupons(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/try-at-home/admin-coupon-suggestions';
      const response = await axiosInstance.post(`${url}`, payload ?? {});
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  }

  async applyTahAdminCoupon(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/try-at-home/admin-coupon-apply';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async assignCourier(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/reship/' + payload.courierPartner;
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async cancelPorter(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/porter/cancel';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async cancelLyskraft(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/lyskraft/cancel';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async cancelShadowfax(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/shadowfax/cancel';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markReceivedAtStore(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/receivedAtStore';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markDeliveredAtStore(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/deliveredAtStore';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markReadyToShip(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/packed';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async completePacking(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/packing/complete';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markDispatched(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/dispatched';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async generateShippingLabel(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/generate-shipping-label';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async generateInvoiceLabel(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/generate-invoice-label';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markUndelivered(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/courier/undelivered';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markUndeliveredReturnedToStore(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/undelivered-returned-to-store';
      const response = await axiosInstance.put(`${url}`, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async vendorRtoReceived(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/rto';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markRTOComplete(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/rto';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async initiateRto(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/rto/initiate';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markOutForDelivery(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/out-for-delivery';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async markArrivedAtLocation(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/arrived-at-location';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async porterStatusSync(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = `${this.baseURL}/${meta?.packageId}/porter/status-sync`;
      const response = await axiosInstance.put(url, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async shadowfaxStatusSync(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = `${this.baseURL}/${meta?.packageId}/shadowfax/status-sync`;
      const response = await axiosInstance.put(url, payload ?? {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async updateDeliveryDetails(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.packageId + '/tat';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async rescheduleCustomerDeliveryEta(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = this.baseURL + '/' + meta?.packageId + '/customer-delivery-eta';
    const response = await axiosInstance.put(url, payload);
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(
        response.data?.status?.message ||
          response.data?.status?.key ||
          'Unable to reschedule customer delivery.',
      );
    }
    return this.transformNode(response.data?.data);
  }

  async switchFacility(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const packageId = meta?.packageId;
      const {
        facilityId,
        lineItemIds,
        expectedSourceMode,
        expectedSourceFacilityId,
        reason,
        ...restPayload
      } = payload ?? {};

      if (!packageId) {
        throw new Error('Package ID is required to switch facility');
      }
      if (!facilityId) {
        throw new Error('facilityId is required to switch facility');
      }

      const normalizedLineItemIds = Array.isArray(lineItemIds)
        ? lineItemIds.map((id: number | string) => Number(id)).filter(id => Number.isFinite(id))
        : [];

      if (!normalizedLineItemIds.length) {
        throw new Error('At least one line item id is required to switch facility');
      }
      if (!expectedSourceMode || !expectedSourceFacilityId) {
        throw new Error('Package source state is required to switch facility');
      }

      const queryParams = new URLSearchParams({
        lineItemIds: normalizedLineItemIds.join(','),
        expectedSourceMode: String(expectedSourceMode),
        expectedSourceFacilityId: String(expectedSourceFacilityId),
      });
      // Optional operator reason, recorded on the package timeline with the facility change.
      if (typeof reason === 'string' && reason.trim()) queryParams.set('reason', reason.trim());
      const queryString = queryParams.toString();

      const url = `${this.baseURL}/${packageId}/switchFacility/${facilityId}?${queryString}`;

      const requestBody = restPayload && Object.keys(restPayload).length ? restPayload : {};

      const response = await axiosInstance.put(url, requestBody);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async switchModeToOnhand(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const packageId = meta?.packageId;
      const {
        facilityId,
        expectedSourceMode,
        expectedSourceFacilityId,
        expectedActiveLineItemIds,
        reason,
      } = payload ?? {};

      if (!packageId) {
        throw new Error('Package ID is required to convert package to ONHAND');
      }
      if (!facilityId) {
        throw new Error('facilityId is required to convert package to ONHAND');
      }

      const convertUrl = `${this.baseURL}/${packageId}/convert-mode`;
      const response = await axiosInstance.put(convertUrl, {
        targetMode: 'ONHAND',
        facilityId: Number(facilityId),
        expectedSourceMode,
        expectedSourceFacilityId,
        expectedActiveLineItemIds,
        ...(typeof reason === 'string' && reason.trim() ? { reason: reason.trim() } : {}),
      });
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async switchModeToMto(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const packageId = meta?.packageId;
      const {
        vendorDeliveryDate,
        reason,
        expectedSourceMode,
        expectedSourceFacilityId,
        expectedActiveLineItemIds,
      } = payload ?? {};

      if (!packageId) {
        throw new Error('Package ID is required to convert package to MTO');
      }

      const convertUrl = `${this.baseURL}/${packageId}/convert-mode`;
      const requestBody = {
        targetMode: 'MTO',
        ...(vendorDeliveryDate ? { vendorDeliveryDate } : {}),
        ...(reason ? { reason } : {}),
        ...(expectedSourceMode ? { expectedSourceMode } : {}),
        ...(expectedSourceFacilityId ? { expectedSourceFacilityId } : {}),
        ...(expectedActiveLineItemIds ? { expectedActiveLineItemIds } : {}),
      };
      const response = await axiosInstance.put(convertUrl, requestBody);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async switchModeToJit(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const packageId = meta?.packageId;
      const {
        facilityId,
        expectedSourceMode,
        expectedSourceFacilityId,
        expectedActiveLineItemIds,
        reason,
      } = payload ?? {};

      if (!packageId) {
        throw new Error('Package ID is required to convert package to JIT');
      }
      if (!facilityId) {
        throw new Error('facilityId is required to convert package to JIT');
      }

      const convertUrl = `${this.baseURL}/${packageId}/convert-mode`;
      const response = await axiosInstance.put(convertUrl, {
        targetMode: 'JIT',
        facilityId: Number(facilityId),
        expectedSourceMode,
        expectedSourceFacilityId,
        expectedActiveLineItemIds,
        ...(typeof reason === 'string' && reason.trim() ? { reason: reason.trim() } : {}),
      });
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async splitPackage(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const packageId = meta?.packageId;
      const { facilityCode, orderLineItemIds, reason } = payload ?? {};

      if (!packageId) {
        throw new Error('Package ID is required to split package');
      }
      if (!facilityCode || typeof facilityCode !== 'string') {
        throw new Error('facilityCode is required to split package');
      }

      const normalizedLineItemIds = Array.isArray(orderLineItemIds)
        ? orderLineItemIds
            .map((id: number | string) => Number(id))
            .filter((id: number) => Number.isFinite(id))
        : [];

      if (!normalizedLineItemIds.length) {
        throw new Error('At least one order line item id is required to split package');
      }

      const url = `${this.baseURL}/${packageId}/split`;
      const response = await axiosInstance.put(url, {
        facilityCode: facilityCode.trim(),
        orderLineItemIds: normalizedLineItemIds,
        // Optional operator reason, recorded on the package timeline with the split.
        ...(typeof reason === 'string' && reason.trim() ? { reason: reason.trim() } : {}),
      });
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }
}

export default OrderPackageAPIClient;
